import React, { useState, useRef } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { BoxLegendSvg } from '@nivo/legends';
import DateSelector from './DateSelector';
import moment from 'moment';
import { useRouter } from 'found';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { SubscriptionUsageQuery } from './__generated__/SubscriptionUsageQuery.graphql';
import styled from 'styled-components';
import { InfoLabel } from './InfoLabel';
import { formatCurrency } from './../helpers';

const Container = styled.div``;

export const Tooltip = styled.div`
  background: white;
  padding: 18px;
  border-radius: 8px;
  box-shadow: 0px 3px 10px #413c8d30;
`;

export const ToolipTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 3px;
  display: flex;
  color: #3f487b;
  justify-content: space-between;
`;

export const TooltipSubtitle = styled.div`
  font-size: 12px;
  color: #9092c1;
  max-width: 200px;
`;

const BarLegend = ({ height, legends, width }: any) => (
  <React.Fragment>
    {legends.map((legend: any) => (
      <BoxLegendSvg
        key={JSON.stringify(legend.data.map(({ id }: any) => id))}
        {...legend}
        containerHeight={height}
        containerWidth={width}
      />
    ))}
  </React.Fragment>
);

function hslToHex(h: number, s: number, l: number) {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: any) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0'); // convert to Hex and prefix "0" if needed
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function hashCode(str: string) {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = hash % 40;
  return Math.abs(hash) + 60;
}

function strToColor(str: string) {
  return hslToHex(233, 92, hashCode(str));
}

export default function SubscriptionUsage() {
  const { match } = useRouter();

  const before = new Date();
  before.setHours(23, 59, 59, 999);

  const after = new Date(before.getTime() - 5 * 24 * 60 * 60 * 1000);
  after.setHours(0, 0, 0, 0);

  const [variables, setVariables] = useState({
    before: before.toISOString(),
    after: after.toISOString(),
    subscriptionID: match.params.subscriptionID,
  });

  const usageData = useLazyLoadQuery<SubscriptionUsageQuery>(
    graphql`
      query SubscriptionUsageQuery(
        $subscriptionID: ID!
        $before: Time!
        $after: Time!
      ) {
        subscription: node(id: $subscriptionID) {
          ... on Subscription {
            usage(before: $before, after: $after, intervalSeconds: 86400) {
              intervals {
                totalCosts {
                  value
                  currency
                }
                startDate
                groupedCharges {
                  charge {
                    id
                    name
                    description
                  }
                  unitsUsed
                  totalCost
                }
              }
            }
          }
        }
      }
    `,
    variables
  );

  var tableData = [...(usageData?.subscription?.usage?.intervals ?? [])];

  const isWeeks = tableData.length > 10;

  var keys = new Set<string>([]);
  let weekData: any[] = [];

  const startWeek = moment(tableData[0].startDate).isoWeek();

  const idToCharge: any = {};
  const data = tableData.map((d, ind) => {
    const k: any = {};
    k.date = new Date(d.startDate);
    // Calculate weekly data
    const i = moment(k.date).isoWeek();
    const currentWeek = i - startWeek;

    if (!weekData[currentWeek]) {
      weekData[currentWeek] = { ...k };
    }

    d.groupedCharges.forEach((chargeGroup) => {
      const chargeID = chargeGroup.charge?.id ?? `Unknown charge`;
      keys.add(chargeID);

      if (k[chargeID]) {
        k[chargeID] = k[chargeID] + chargeGroup.unitsUsed;
      } else {
        k[chargeID] = chargeGroup.unitsUsed;
      }

      idToCharge[chargeID] = chargeGroup.charge;

      if (weekData[currentWeek][chargeID]) {
        weekData[currentWeek][chargeID] =
          chargeGroup.unitsUsed + weekData[currentWeek][chargeID];
      } else {
        weekData[currentWeek][chargeID] = chargeGroup.unitsUsed;
      }
    });

    k['totalCosts'] = d.totalCosts;

    return k;
  });

  const keyArray = Array.from(keys.keys());

  const [startDate, setStartDate] = useState<string | null>(
    moment(data[0].date).toISOString()
  );
  const [endDate, setEndDate] = useState<string | null>(
    moment(data[data.length - 1].date).toISOString()
  );

  const updateVars = (
    start: moment.Moment | null,
    end: moment.Moment | null
  ) => {
    const newVars = { ...variables };
    if (start) {
      newVars.after = start.startOf('day').toISOString();
    }

    if (end) {
      newVars.before = end.endOf('day').toISOString();
    }

    if (moment(newVars.before).unix() - moment(newVars.after).unix() < 0) {
      return;
    }

    setVariables(newVars);
  };

  const CustomBarComponent = (props: any) => {
    const {
      x,
      y,
      width,
      height,
      color,
      data,
      showTooltip,
      hideTooltip,
    } = props;
    const overlap = y + height >= 300 ? 0 : 10;
    const hovering = useRef(false);
    return (
      <g
        transform={`translate(${x}, ${y})`}
        onMouseMove={(event): void => {
          showTooltip(
            <Tooltip>
              <ToolipTitle>
                <div>{idToCharge[data.id]?.name ?? 'Unknown Charge'}</div>
              </ToolipTitle>
              {/* <TooltipSubtitle>
                {idToCharge[data.id]?.description ?? ''}
              </TooltipSubtitle> */}
              {/* <InfoLabel label={'Requests'}>13</InfoLabel> */}
              <InfoLabel label={'Units used'}>{data.value}</InfoLabel>
              {data.data.totalCosts.map((currency: any) => (
                <InfoLabel label={`Total`}>
                  {formatCurrency(currency.currency, currency.value)}
                </InfoLabel>
              ))}
            </Tooltip>,
            event
          );
        }}
        onMouseLeave={(event): void => {
          hideTooltip();
        }}
      >
        {height > 0 && (
          <path
            id='Rectangle_969'
            data-name='Rectangle 969'
            d={`M8,0H${width}a8,8,0,0,1,8,8V${
              height + overlap
            }a0,0,0,0,1,0,0H0a0,0,0,0,1,0,0V8A8,8,0,0,1,8,0Z`}
            fill={color}
            style={{
              filter: hovering.current
                ? 'drop-shadow(0px 3px 33px #00000021) brightness(1.05)'
                : '',
            }}
            onMouseOver={() => {
              hovering.current = true;
            }}
            onMouseLeave={() => (hovering.current = false)}
          />
        )}
      </g>
    );
  };
  return (
    <Container>
      <DateSelector
        startDate={startDate}
        endDate={endDate}
        onDatesChange={({ startDate, endDate, forceRender }) => {
          setStartDate(startDate?.toISOString() ?? null);
          setEndDate(endDate?.toISOString() ?? null);

          if (forceRender) {
            updateVars(startDate, endDate);
          }
        }}
        onClose={() => {
          const end = moment(endDate);
          const start = moment(startDate);

          if (!end?.isValid() || !start?.isValid()) {
            return;
          }
          if (end?.isBefore(start)) {
            return;
          }

          updateVars(start, end);
        }}
      />
      <div style={{ height: 400 }}>
        <ResponsiveBar
          data={isWeeks ? weekData : data}
          keys={keyArray}
          indexBy={'date'}
          margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
          padding={0.1}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={(info) => {
            return strToColor(info.id);
          }}
          barComponent={CustomBarComponent}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            renderTick: (tick: any) => {
              if (isWeeks) {
                return (
                  <g transform={`translate(${tick.x},${tick.y + 22})`}>
                    <text
                      textAnchor='middle'
                      dominantBaseline='middle'
                      style={{
                        fill: '#333',
                        fontSize: 12,
                      }}
                    >
                      Week {moment(tick.value).isoWeek()}
                    </text>
                    <text
                      textAnchor='middle'
                      y={20}
                      dominantBaseline='middle'
                      style={{
                        fill: '#333',
                        fontSize: 10,
                      }}
                    >
                      {moment(tick.value).startOf('isoWeek').format('DD/MM')}
                    </text>
                  </g>
                );
              }

              return (
                <g transform={`translate(${tick.x},${tick.y + 22})`}>
                  <text
                    textAnchor='middle'
                    dominantBaseline='middle'
                    style={{
                      fill: '#333',
                      fontSize: 12,
                    }}
                  >
                    {tick.value.toLocaleDateString('en-gb', {
                      weekday: 'short',
                    })}
                  </text>
                  <text
                    textAnchor='middle'
                    y={20}
                    dominantBaseline='middle'
                    style={{
                      fill: '#333',
                      fontSize: 10,
                    }}
                  >
                    {tick.value.toLocaleDateString('en-gb', {
                      month: 'numeric',
                      day: 'numeric',
                    })}
                  </text>
                </g>
              );
            },
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 2,
            tickRotation: 0,
            legend: 'Units used',
            legendPosition: 'middle',
            legendOffset: -40,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          layers={['grid', 'axes', 'bars', 'markers', BarLegend]}
          legends={[
            {
              dataFrom: 'keys',
              data: keyArray.map((id, index) => ({
                color: strToColor(id),
                id: idToCharge[id]?.name ?? id,
                label: idToCharge[id]?.name ?? 'Unknown Charge',
              })),
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 20,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
          animate={false}
          motionStiffness={90}
          motionDamping={15}
        />
      </div>
    </Container>
  );
}
